import React, { useState, useEffect } from "react";
import { Patient } from "../../PatientCard";
import BodyMapService, {
  BodyRegion,
  BodyLocation,
  CreateBodyMapFindingData,
  BodyMapFormOptions,
} from "../../../../services/bodyMapService";

interface BodyMapModalProps {
  patient: Patient;
  isOpen: boolean;
  onClose: () => void;
  onFindingsSaved?: (patientId: number, findingsCount: number) => void;
}

interface SelectedFinding {
  body_location_id: number;
  body_location?: BodyLocation;
  laterality_specific?: "left" | "right" | "bilateral" | "central";
  finding_type: "injury" | "symptom" | "examination" | "procedure" | "normal";
  severity?: "mild" | "moderate" | "severe" | "critical";
  description: string;
  clinical_notes?: string;
  pain_scale?: number;
  size_measurement?: string;
  color_description?: string;
  temperature?: "normal" | "warm" | "hot" | "cool" | "cold";
  mobility?: "normal" | "limited" | "fixed" | "hypermobile";
  tenderness?: "none" | "mild" | "moderate" | "severe";
  swelling?: "none" | "mild" | "moderate" | "severe";
  follow_up_required: boolean;
  follow_up_date?: string;
}

const BodyMapModal: React.FC<BodyMapModalProps> = ({
  patient,
  isOpen,
  onClose,
  onFindingsSaved,
}) => {
  const [bodyRegions, setBodyRegions] = useState<BodyRegion[]>([]);
  const [formOptions, setFormOptions] = useState<BodyMapFormOptions | null>(
    null
  );
  const [selectedFindings, setSelectedFindings] = useState<SelectedFinding[]>(
    []
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<BodyLocation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeRegion, setActiveRegion] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedFindings, setExpandedFindings] = useState<Set<number>>(
    new Set()
  );

  // Detect mobile screen size
  // Detect mobile screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      // Better mobile/tablet detection
      const isTouchDevice =
        "ontouchstart" in window || navigator.maxTouchPoints > 0;
      const mobile = width < 768 || (isTouchDevice && width < 900);
      const tablet =
        (!mobile && width >= 768 && width < 1024) ||
        (isTouchDevice && width >= 900 && width < 1200);

      setIsMobile(mobile);
      setIsTablet(tablet);
      setSidebarCollapsed(mobile || tablet);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Load initial data when modal opens
  useEffect(() => {
    if (isOpen) {
      loadBodyRegions();
      loadFormOptions();
    }
  }, [isOpen]);

  // Search locations with debounce
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchQuery.trim().length >= 2) {
        setSearchLoading(true);
        try {
          const results = await BodyMapService.searchLocations({
            search: searchQuery,
            limit: 20,
          });
          setSearchResults(results);
        } catch (error) {
          console.error("Location search failed:", error);
          setSearchResults([]);
        } finally {
          setSearchLoading(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Clear state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedFindings([]);
      setSearchQuery("");
      setSearchResults([]);
      setActiveRegion(null);
      setErrors({});
    }
  }, [isOpen]);
  const toggleFindingExpansion = (index: number) => {
    setExpandedFindings((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };
  const loadBodyRegions = async () => {
    setIsLoading(true);
    try {
      const regions = await BodyMapService.getBodyRegions(true);
      setBodyRegions(regions);
      if (regions.length > 0) {
        setActiveRegion(regions[0].id);
      }
    } catch (error) {
      console.error("Failed to load body regions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadFormOptions = async () => {
    try {
      const options = await BodyMapService.getFormOptions();
      setFormOptions(options);
    } catch (error) {
      console.error("Failed to load form options:", error);
    }
  };

  const addLocationToFindings = (location: BodyLocation) => {
    // Check if location already added
    if (
      selectedFindings.some(
        (finding) => finding.body_location_id === location.id
      )
    ) {
      setErrors({
        location: "This body location is already added to the findings",
      });
      return;
    }

    const newFinding: SelectedFinding = {
      body_location_id: location.id,
      body_location: location,
      finding_type: "examination",
      description: "",
      follow_up_required: false,
    };

    setSelectedFindings((prev) => [...prev, newFinding]);
    setSearchQuery("");
    setSearchResults([]);
    setErrors({});

    // Close sidebar on mobile after adding
    if (isMobile) {
      setSidebarCollapsed(true);
    }
  };

  const addLocationFromRegion = (location: BodyLocation) => {
    addLocationToFindings(location);
  };

  const updateFinding = (
    index: number,
    field: keyof SelectedFinding,
    value: any
  ) => {
    setSelectedFindings((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeFinding = (index: number) => {
    setSelectedFindings((prev) => prev.filter((_, i) => i !== index));
  };

  const validateFindings = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (selectedFindings.length === 0) {
      newErrors.findings = "Please add at least one body location finding";
    }

    // Validate each finding
    // selectedFindings.forEach((finding, index) => {
    //   if (!finding.description.trim()) {
    //     newErrors[`finding_${index}_description`] =
    //       "Description is required for each finding";
    //   }
    // });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateFindings()) return;

    setIsSaving(true);
    try {
      const findingsData: CreateBodyMapFindingData = {
        patient_id: patient.id,
        findings: selectedFindings.map((finding) => ({
          body_location_id: finding.body_location_id,
          laterality_specific: finding.laterality_specific,
          finding_type: finding.finding_type,
          severity: finding.severity,
          description: finding.description,
          clinical_notes: finding.clinical_notes,
          pain_scale: finding.pain_scale,
          size_measurement: finding.size_measurement,
          color_description: finding.color_description,
          temperature: finding.temperature,
          mobility: finding.mobility,
          tenderness: finding.tenderness,
          swelling: finding.swelling,
          follow_up_required: finding.follow_up_required,
          follow_up_date: finding.follow_up_date,
        })),
      };

      const result = await BodyMapService.savePatientFindings(findingsData);

      if (onFindingsSaved) {
        onFindingsSaved(patient.id, result.findings_count);
      }

      onClose();
    } catch (error: any) {
      setErrors({
        submit: error.message || "Failed to save body map findings",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    zIndex: 1000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: isMobile ? "0" : isTablet ? "16px" : "20px",
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: "#ffffff",
    borderRadius: isMobile ? "0" : "16px",
    width: "100%",
    height: isMobile ? "70vh" : isTablet ? "70vh" : "auto",
    maxHeight: "70vh", // Changed: enforce max height for all devices
    maxWidth: isMobile ? "100%" : isTablet ? "95%" : "1200px",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
    overflow: "hidden",
    position: "relative",
  };

  const headerStyle: React.CSSProperties = {
    padding: isMobile ? "16px" : isTablet ? "18px" : "20px",
    borderBottom: "1px solid #e5e7eb",
    backgroundColor: "#f8fafc",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: isMobile ? "12px" : isTablet ? "14px" : "16px",
    flexShrink: 0,
  };

  const contentStyle: React.CSSProperties = {
    flex: 1,
    display: "flex",
    flexDirection: "column", // Changed: always column for mobile AND tablet
    overflow: "hidden",
    minHeight: 0,
    height: "100%",
  };

  const footerStyle: React.CSSProperties = {
    padding: "20px",
    borderTop: "1px solid #e5e7eb",
    backgroundColor: "#f8fafc",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: isMobile ? "column" : "row",
    gap: isMobile ? "16px" : "0",
    flexShrink: 0,
  };

  const sidebarStyle: React.CSSProperties = {
    width: "100%",
    height: isMobile
      ? sidebarCollapsed
        ? "auto"
        : "40vh"
      : isTablet
      ? "350px"
      : "400px",
    borderRight: isMobile || isTablet ? "none" : "1px solid #e5e7eb",
    borderBottom: isMobile || isTablet ? "1px solid #e5e7eb" : "none",
    backgroundColor: "#f8fafc",
    padding: isMobile ? "16px" : "20px",
    overflow: "visible",
    flexShrink: 0,
  };

  const mainContentStyle: React.CSSProperties = {
    flex: 1,
    padding: isMobile ? "16px" : isTablet ? "20px" : "24px",
    overflow: "auto",
    height: "auto", // Changed for tablet
    minHeight: "200px", // Changed for tablet
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: isMobile ? "10px 12px" : isTablet ? "9px 12px" : "8px 12px",
    border: "2px solid #e5e7eb",
    borderRadius: "6px",
    fontSize: isMobile ? "16px" : isTablet ? "15px" : "14px", // Prevent zoom on iOS
    outline: "none",
    transition: "border-color 0.2s ease",
  };

  const buttonStyle = (
    variant: "primary" | "secondary" | "danger" = "primary"
  ): React.CSSProperties => {
    const variants = {
      primary: { background: "#3b82f6", color: "white" },
      secondary: { background: "#e5e7eb", color: "#374151" },
      danger: { background: "#ef4444", color: "white" },
    };

    return {
      padding: isMobile ? "12px 16px" : "8px 16px",
      borderRadius: "6px",
      border: "none",
      fontSize: isMobile ? "16px" : "14px",
      fontWeight: "500",
      cursor: "pointer",
      transition: "all 0.2s ease",
      minHeight: isMobile ? "44px" : "auto", // Touch target size
      ...variants[variant],
    };
  };

  return (
    <div
      style={overlayStyle}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div style={modalStyle}>
        {/* Header */}
        <div style={headerStyle} className="nn">
          <div style={{ flex: 1 }}>
            <h2
              style={{
                fontSize: isMobile ? "18px" : isTablet ? "19px" : "20px",
                fontWeight: "600",
                margin: "0 0 4px 0",
                color: "#1f2937",
              }}
            >
              🗺️ Body Map Examination
            </h2>
            <p
              style={{
                fontSize: isMobile ? "13px" : isTablet ? "13px" : "14px",
                color: "#64748b",
                margin: 0,
              }}
            >
              Select body locations for{" "}
              {patient.full_name ||
                `${patient.first_name} ${patient.last_name}`}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              ...buttonStyle("secondary"),
              padding: isMobile
                ? "8px 12px"
                : isTablet
                ? "8px 12px"
                : "8px 12px",
              fontSize: isMobile ? "16px" : isTablet ? "15px" : "16px",
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>

        {/* Mobile/Tablet sidebar toggle */}
        {(isMobile || isTablet) && (
          <div
            style={{
              padding: isMobile ? "16px" : "18px",
              borderBottom: "1px solid #e5e7eb",
              backgroundColor: "#f8fafc",
            }}
          >
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              style={{
                ...buttonStyle("secondary"),
                width: "100%",
                padding: isMobile ? "12px 16px" : "10px 16px",
                fontSize: isMobile ? "16px" : "15px",
              }}
            >
              {sidebarCollapsed
                ? "Show Search & Regions"
                : "Hide Search & Regions"}
            </button>
          </div>
        )}

        {/* Mobile sidebar toggle */}
        {/* {isMobile && (
          <div
            style={{
              padding: "16px",
              borderBottom: "1px solid #e5e7eb",
              backgroundColor: "#f8fafc",
            }}
          >
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              style={{
                ...buttonStyle("secondary"),
                width: "100%",
              }}
            >
              {sidebarCollapsed
                ? "Show Search & Regions"
                : "Hide Search & Regions"}
            </button>
          </div>
        )} */}

        <div style={contentStyle}>
          {/* Sidebar - Body Regions & Search */}
          {(!isMobile && !isTablet) || !sidebarCollapsed ? (
            <div style={sidebarStyle}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: isMobile ? "12px" : isTablet ? "16px" : "24px",
                  height: "100%",
                  overflow: "auto", // Prevent vertical scroll
                  maxHeight: isMobile ? "none" : isTablet ? "30vh" : "35vh",
                }}
              >
                {/* Left column - Search and Browse */}
                <div
                  style={{
                    flex: "0 0 50%",
                    display: "flex",
                    flexDirection: "column",
                    paddingRight: isTablet ? "8px" : isMobile ? "6px" : "0",
                  }}
                >
                  {/* Search */}
                  <div style={{ marginBottom: "20px" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: isMobile ? "15px" : "14px",
                        fontWeight: "500",
                        marginBottom: "8px",
                      }}
                    >
                      Search Body Locations
                    </label>
                    <div style={{ position: "relative" }}>
                      <input
                        type="text"
                        id="search_anatomical_location"
                        name="search_anatomical_location"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search for anatomical locations..."
                        style={inputStyle}
                      />

                      {searchLoading && (
                        <div
                          style={{
                            position: "absolute",
                            right: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                          }}
                        >
                          🔄
                        </div>
                      )}
                    </div>

                    {/* Search Results */}
                    {searchResults.length > 0 && (
                      <div
                        style={{
                          marginTop: "8px",
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          maxHeight: isMobile ? "150px" : "200px",
                          overflow: "auto",
                          backgroundColor: "white",
                        }}
                      >
                        {searchResults.map((location) => (
                          <div
                            key={location.id}
                            onClick={() => addLocationToFindings(location)}
                            style={{
                              padding: isMobile ? "16px 12px" : "12px",
                              borderBottom: "1px solid #f3f4f6",
                              cursor: "pointer",
                              transition: "background-color 0.2s ease",
                              minHeight: isMobile ? "60px" : "auto",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                "#f8fafc")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.backgroundColor =
                                "transparent")
                            }
                          >
                            <div
                              style={{ fontWeight: "500", marginBottom: "4px" }}
                            >
                              {location.display_name}
                            </div>
                            <div
                              style={{
                                fontSize: isMobile ? "13px" : "12px",
                                color: "#64748b",
                              }}
                            >
                              {location.region_name}
                              {location.laterality !== "none" &&
                                ` • ${location.laterality} side available`}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {errors.location && (
                      <div
                        style={{
                          color: "#ef4444",
                          fontSize: isMobile ? "13px" : "12px",
                          marginTop: "4px",
                        }}
                      >
                        {errors.location}
                      </div>
                    )}
                  </div>

                  {/* Body Regions */}
                  <div>
                    <label
                      style={{
                        display: "block",
                        fontSize: isMobile ? "15px" : "14px",
                        fontWeight: "500",
                        marginBottom: "12px",
                      }}
                    >
                      Browse by Body Region
                    </label>

                    {/* Region Tabs */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        flexWrap: "nowrap",
                        gap: "4px",
                        marginBottom: "16px",
                      }}
                    >
                      {bodyRegions.map((region) => (
                        <button
                          key={region.id}
                          onClick={() => setActiveRegion(region.id)}
                          style={{
                            ...buttonStyle(
                              activeRegion === region.id
                                ? "primary"
                                : "secondary"
                            ),
                            textAlign: "left",
                            fontSize: isMobile ? "14px" : "13px",
                            padding: isMobile ? "12px" : "8px 12px",
                          }}
                        >
                          {region.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Right column - Locations in Active Region */}
                <div
                  style={{
                    width: "50%",
                    flex: "0 0 50%",
                    paddingLeft: isTablet ? "8px" : isMobile ? "6px" : "0",
                  }}
                >
                  {activeRegion && (
                    <>
                      {(() => {
                        const region = bodyRegions.find(
                          (r) => r.id === activeRegion
                        );
                        if (!region || !region.locations) return null;

                        return (
                          <div>
                            <h4
                              style={{
                                fontSize: isMobile ? "15px" : "14px",
                                fontWeight: "600",
                                marginBottom: "8px",
                                color: "#1f2937",
                              }}
                            >
                              {region.name} Locations:
                            </h4>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                              }}
                            >
                              {region.locations.map((location) => (
                                <div
                                  key={location.id}
                                  onClick={() =>
                                    addLocationFromRegion(location)
                                  }
                                  style={{
                                    padding: isMobile
                                      ? "16px 12px"
                                      : "8px 12px",
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    backgroundColor: selectedFindings.some(
                                      (f) => f.body_location_id === location.id
                                    )
                                      ? "#dbeafe"
                                      : "white",
                                    minHeight: isMobile ? "60px" : "auto",
                                  }}
                                  onMouseEnter={(e) => {
                                    if (
                                      !selectedFindings.some(
                                        (f) =>
                                          f.body_location_id === location.id
                                      )
                                    ) {
                                      e.currentTarget.style.backgroundColor =
                                        "#f8fafc";
                                      e.currentTarget.style.borderColor =
                                        "#3b82f6";
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (
                                      !selectedFindings.some(
                                        (f) =>
                                          f.body_location_id === location.id
                                      )
                                    ) {
                                      e.currentTarget.style.backgroundColor =
                                        "white";
                                      e.currentTarget.style.borderColor =
                                        "#e5e7eb";
                                    }
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: isMobile ? "14px" : "13px",
                                      fontWeight: "500",
                                    }}
                                  >
                                    {location.display_name}
                                  </div>
                                  {location.anatomical_notes && (
                                    <div
                                      style={{
                                        fontSize: isMobile ? "12px" : "11px",
                                        color: "#6b7280",
                                        marginTop: "2px",
                                      }}
                                    >
                                      {location.anatomical_notes}
                                    </div>
                                  )}
                                  {selectedFindings.some(
                                    (f) => f.body_location_id === location.id
                                  ) && (
                                    <div
                                      style={{
                                        fontSize: isMobile ? "12px" : "11px",
                                        color: "#3b82f6",
                                        marginTop: "2px",
                                      }}
                                    >
                                      ✓ Added to findings
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : null}
          {/* Main Content - Selected Findings */}
          <div className="nn" style={mainContentStyle}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
                flexDirection: isMobile ? "column" : "row",
                gap: isMobile ? "12px" : "0",
              }}
            >
              <h3
                style={{
                  fontSize: isMobile ? "16px" : "18px",
                  fontWeight: "600",
                  margin: 0,
                  color: "#1f2937",
                  textAlign: isMobile ? "center" : "left",
                }}
              >
                Selected Findings ({selectedFindings.length})
              </h3>
            </div>

            {errors.findings && (
              <div
                style={{
                  color: "#ef4444",
                  fontSize: isMobile ? "15px" : "14px",
                  marginBottom: "16px",
                  padding: isMobile ? "16px" : "12px",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "6px",
                }}
              >
                {errors.findings}
              </div>
            )}

            {selectedFindings.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: isMobile ? "40px 20px" : "60px 20px",
                  color: "#6b7280",
                }}
              >
                <div
                  style={{
                    fontSize: isMobile ? "32px" : isTablet ? "40px" : "48px",
                    marginBottom: "16px",
                  }}
                >
                  🗺️
                </div>
                <h4
                  style={{
                    fontSize: isMobile ? "16px" : isTablet ? "17px" : "18px",
                    marginBottom: "8px",
                  }}
                >
                  No Body Locations Selected
                </h4>
                <p
                  style={{
                    fontSize: isMobile ? "15px" : isTablet ? "14px" : "14px",
                    lineHeight: "1.5",
                  }}
                >
                  Use the search bar or browse body regions{" "}
                  {isMobile ? "above" : "on the left"} to add anatomical
                  locations to your examination findings.
                </p>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                {selectedFindings.map((finding, index) => (
                  <div
                    key={index}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: "12px",
                      overflow: "hidden",
                    }}
                  >
                    {/* Finding Header */}
                    <div
                      className="nn"
                      style={{
                        background: "#f8fafc",
                        padding: isMobile ? "20px" : "16px",
                        borderBottom: "1px solid #e5e7eb",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: isMobile ? "flex-start" : "center",
                        flexDirection: isMobile ? "column" : "row",
                        gap: isMobile ? "12px" : "0",
                      }}
                    >
                      <div style={{ width: isMobile ? "100%" : "auto" }}>
                        <h4
                          style={{
                            fontSize: isMobile ? "15px" : "16px",
                            fontWeight: "600",
                            margin: 0,
                            marginBottom: "4px",
                          }}
                        >
                          {finding.body_location?.display_name}
                        </h4>
                        <p
                          style={{
                            fontSize: isMobile ? "13px" : "12px",
                            color: "#6b7280",
                            margin: 0,
                          }}
                        >
                          {finding.body_location?.region_name}
                        </p>
                      </div>
                      <button
                        onClick={() => removeFinding(index)}
                        style={{
                          ...buttonStyle("danger"),
                          padding: isMobile ? "10px 16px" : "6px 12px",
                          fontSize: isMobile ? "14px" : "12px",
                          width: isMobile ? "100%" : "auto",
                        }}
                      >
                        🗑️ Remove
                      </button>
                    </div>

                    {/* Finding Form */}
                    <div
                      className="nn"
                      style={{
                        flexDirection: isMobile ? "column" : "row",
                        padding: isMobile ? "16px" : "20px",
                      }}
                    >
                      {/* Toggle Button */}
                      <button
                        className="nn"
                        type="button"
                        onClick={() => toggleFindingExpansion(index)}
                        style={{
                          ...buttonStyle("secondary"),
                          width: "100%",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: expandedFindings.has(index)
                            ? "16px"
                            : "0",
                        }}
                      >
                        <span
                          className="nn"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span>{expandedFindings.has(index) ? "▼" : "▶"}</span>
                          <span>Finding Details</span>
                        </span>
                        <span
                          style={{
                            fontSize: isMobile ? "13px" : "12px",
                            color: "#6b7280",
                          }}
                        >
                          {expandedFindings.has(index) ? "Hide" : "Show"}{" "}
                          optional fields
                        </span>
                      </button>

                      {/* Expandable Content */}
                      {expandedFindings.has(index) && (
                        <>
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: isMobile
                                ? "1fr"
                                : "repeat(auto-fit, minmax(250px, 1fr))",
                              gap: "16px",
                              marginBottom: "16px",
                            }}
                          >
                            {/* Laterality Specific */}
                            {finding.body_location?.supports_laterality && (
                              <div>
                                <label
                                  style={{
                                    display: "block",
                                    fontSize: isMobile ? "15px" : "14px",
                                    fontWeight: "500",
                                    marginBottom: "6px",
                                  }}
                                >
                                  Side
                                </label>
                                <select
                                  value={finding.laterality_specific || ""}
                                  onChange={(e) =>
                                    updateFinding(
                                      index,
                                      "laterality_specific",
                                      e.target.value as any
                                    )
                                  }
                                  style={inputStyle}
                                >
                                  <option value="">Select side</option>
                                  {formOptions &&
                                    Object.entries(
                                      formOptions.laterality_specific
                                    ).map(([key, label]) => (
                                      <option key={key} value={key}>
                                        {label}
                                      </option>
                                    ))}
                                </select>
                              </div>
                            )}

                            {/* Finding Type */}
                            <div>
                              <label
                                style={{
                                  display: "block",
                                  fontSize: isMobile ? "15px" : "14px",
                                  fontWeight: "500",
                                  marginBottom: "6px",
                                }}
                              >
                                Finding Type
                              </label>
                              <select
                                value={finding.finding_type}
                                onChange={(e) =>
                                  updateFinding(
                                    index,
                                    "finding_type",
                                    e.target.value as any
                                  )
                                }
                                style={inputStyle}
                              >
                                {formOptions &&
                                  Object.entries(formOptions.finding_types).map(
                                    ([key, label]) => (
                                      <option key={key} value={key}>
                                        {label}
                                      </option>
                                    )
                                  )}
                              </select>
                            </div>

                            {/* Severity */}
                            <div>
                              <label
                                style={{
                                  display: "block",
                                  fontSize: isMobile ? "15px" : "14px",
                                  fontWeight: "500",
                                  marginBottom: "6px",
                                }}
                              >
                                Severity
                              </label>
                              <select
                                value={finding.severity || ""}
                                onChange={(e) =>
                                  updateFinding(
                                    index,
                                    "severity",
                                    e.target.value as any
                                  )
                                }
                                style={inputStyle}
                              >
                                <option value="">Select severity</option>
                                {formOptions &&
                                  Object.entries(formOptions.severities).map(
                                    ([key, label]) => (
                                      <option key={key} value={key}>
                                        {label}
                                      </option>
                                    )
                                  )}
                              </select>
                            </div>

                            {/* Pain Scale */}
                            <div>
                              <label
                                style={{
                                  display: "block",
                                  fontSize: isMobile ? "15px" : "14px",
                                  fontWeight: "500",
                                  marginBottom: "6px",
                                }}
                              >
                                Pain Scale (0-10)
                              </label>
                              <input
                                type="number"
                                id={`pain_scale_${index}`}
                                name={`pain_scale_${index}`}
                                min="0"
                                max="10"
                                value={finding.pain_scale || ""}
                                onChange={(e) =>
                                  updateFinding(
                                    index,
                                    "pain_scale",
                                    e.target.value
                                      ? parseInt(e.target.value)
                                      : undefined
                                  )
                                }
                                style={inputStyle}
                                placeholder="0 = No pain, 10 = Severe pain"
                              />
                            </div>
                          </div>

                          {/* Description */}
                          <div style={{ marginBottom: "16px" }}>
                            <label
                              style={{
                                display: "block",
                                fontSize: isMobile ? "15px" : "14px",
                                fontWeight: "500",
                                marginBottom: "6px",
                              }}
                            >
                              Description
                            </label>
                            <textarea
                              value={finding.description}
                              onChange={(e) =>
                                updateFinding(
                                  index,
                                  "description",
                                  e.target.value
                                )
                              }
                              placeholder="Describe the finding, symptoms, or examination results..."
                              rows={isMobile ? 4 : 3}
                              style={{
                                ...inputStyle,
                                borderColor: "#e5e7eb",
                                resize: "none",
                              }}
                            />
                          </div>

                          {/* Additional Details */}
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: isMobile
                                ? "1fr"
                                : "repeat(auto-fit, minmax(200px, 1fr))",
                              gap: "16px",
                              marginBottom: "16px",
                            }}
                          >
                            {/* Size */}
                            <div>
                              <label
                                style={{
                                  display: "block",
                                  fontSize: isMobile ? "14px" : "13px",
                                  fontWeight: "500",
                                  marginBottom: "6px",
                                }}
                              >
                                Size/Measurement
                              </label>
                              <input
                                type="text"
                                id={`size_measurement_${index}`}
                                name={`size_measurement_${index}`}
                                value={finding.size_measurement || ""}
                                onChange={(e) =>
                                  updateFinding(
                                    index,
                                    "size_measurement",
                                    e.target.value
                                  )
                                }
                                placeholder="e.g., 2cm x 3cm"
                                style={{
                                  ...inputStyle,
                                  fontSize: isMobile ? "16px" : "13px",
                                  padding: isMobile ? "10px 12px" : "6px 10px",
                                }}
                              />
                            </div>

                            {/* Color */}
                            <div>
                              <label
                                style={{
                                  display: "block",
                                  fontSize: isMobile ? "14px" : "13px",
                                  fontWeight: "500",
                                  marginBottom: "6px",
                                }}
                              >
                                Color
                              </label>
                              <input
                                type="text"
                                id={`color_description_${index}`}
                                name={`color_description_${index}`}
                                value={finding.color_description || ""}
                                onChange={(e) =>
                                  updateFinding(
                                    index,
                                    "color_description",
                                    e.target.value
                                  )
                                }
                                placeholder="e.g., red, purple, pale"
                                style={{
                                  ...inputStyle,
                                  fontSize: isMobile ? "16px" : "13px",
                                  padding: isMobile ? "10px 12px" : "6px 10px",
                                }}
                              />
                            </div>

                            {/* Temperature */}
                            <div>
                              <label
                                style={{
                                  display: "block",
                                  fontSize: isMobile ? "14px" : "13px",
                                  fontWeight: "500",
                                  marginBottom: "6px",
                                }}
                              >
                                Temperature
                              </label>
                              <select
                                value={finding.temperature || ""}
                                onChange={(e) =>
                                  updateFinding(
                                    index,
                                    "temperature",
                                    e.target.value as any
                                  )
                                }
                                style={{
                                  ...inputStyle,
                                  fontSize: isMobile ? "16px" : "13px",
                                  padding: isMobile ? "10px 12px" : "6px 10px",
                                }}
                              >
                                <option value="">Select temperature</option>
                                {formOptions &&
                                  Object.entries(formOptions.temperatures).map(
                                    ([key, label]) => (
                                      <option key={key} value={key}>
                                        {label}
                                      </option>
                                    )
                                  )}
                              </select>
                            </div>

                            {/* Tenderness */}
                            <div>
                              <label
                                style={{
                                  display: "block",
                                  fontSize: isMobile ? "14px" : "13px",
                                  fontWeight: "500",
                                  marginBottom: "6px",
                                }}
                              >
                                Tenderness
                              </label>
                              <select
                                value={finding.tenderness || ""}
                                onChange={(e) =>
                                  updateFinding(
                                    index,
                                    "tenderness",
                                    e.target.value as any
                                  )
                                }
                                style={{
                                  ...inputStyle,
                                  fontSize: isMobile ? "16px" : "13px",
                                  padding: isMobile ? "10px 12px" : "6px 10px",
                                }}
                              >
                                <option value="">Select tenderness</option>
                                {formOptions &&
                                  Object.entries(formOptions.tenderness).map(
                                    ([key, label]) => (
                                      <option key={key} value={key}>
                                        {label}
                                      </option>
                                    )
                                  )}
                              </select>
                            </div>

                            {/* Swelling */}
                            <div>
                              <label
                                style={{
                                  display: "block",
                                  fontSize: isMobile ? "14px" : "13px",
                                  fontWeight: "500",
                                  marginBottom: "6px",
                                }}
                              >
                                Swelling
                              </label>
                              <select
                                value={finding.swelling || ""}
                                onChange={(e) =>
                                  updateFinding(
                                    index,
                                    "swelling",
                                    e.target.value as any
                                  )
                                }
                                style={{
                                  ...inputStyle,
                                  fontSize: isMobile ? "16px" : "13px",
                                  padding: isMobile ? "10px 12px" : "6px 10px",
                                }}
                              >
                                <option value="">Select swelling</option>
                                {formOptions &&
                                  Object.entries(formOptions.swelling).map(
                                    ([key, label]) => (
                                      <option key={key} value={key}>
                                        {label}
                                      </option>
                                    )
                                  )}
                              </select>
                            </div>

                            {/* Mobility */}
                            <div>
                              <label
                                style={{
                                  display: "block",
                                  fontSize: isMobile ? "14px" : "13px",
                                  fontWeight: "500",
                                  marginBottom: "6px",
                                }}
                              >
                                Mobility
                              </label>
                              <select
                                value={finding.mobility || ""}
                                onChange={(e) =>
                                  updateFinding(
                                    index,
                                    "mobility",
                                    e.target.value as any
                                  )
                                }
                                style={{
                                  ...inputStyle,
                                  fontSize: isMobile ? "16px" : "13px",
                                  padding: isMobile ? "10px 12px" : "6px 10px",
                                }}
                              >
                                <option value="">Select mobility</option>
                                {formOptions &&
                                  Object.entries(formOptions.mobility).map(
                                    ([key, label]) => (
                                      <option key={key} value={key}>
                                        {label}
                                      </option>
                                    )
                                  )}
                              </select>
                            </div>
                          </div>

                          {/* Clinical Notes */}
                          <div style={{ marginBottom: "16px" }}>
                            <label
                              style={{
                                display: "block",
                                fontSize: isMobile ? "15px" : "14px",
                                fontWeight: "500",
                                marginBottom: "6px",
                              }}
                            >
                              Clinical Notes
                            </label>
                            <textarea
                              value={finding.clinical_notes || ""}
                              onChange={(e) =>
                                updateFinding(
                                  index,
                                  "clinical_notes",
                                  e.target.value
                                )
                              }
                              placeholder="Additional clinical observations, recommendations, or notes..."
                              rows={isMobile ? 3 : 2}
                              style={{ ...inputStyle, resize: "none" }}
                            />
                          </div>

                          {/* Follow-up */}
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: isMobile
                                ? "1fr"
                                : "auto 1fr",
                              gap: "16px",
                              alignItems: isMobile ? "stretch" : "end",
                            }}
                          >
                            <div>
                              <label
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  fontSize: isMobile ? "15px" : "14px",
                                  fontWeight: "500",
                                  cursor: "pointer",
                                  minHeight: isMobile ? "44px" : "auto",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  id={`follow_up_required_${index}`}
                                  name={`follow_up_required_${index}`}
                                  checked={finding.follow_up_required}
                                  onChange={(e) =>
                                    updateFinding(
                                      index,
                                      "follow_up_required",
                                      e.target.checked
                                    )
                                  }
                                  style={{
                                    width: isMobile ? "20px" : "16px",
                                    height: isMobile ? "20px" : "16px",
                                  }}
                                />
                                Requires Follow-up
                              </label>
                            </div>

                            {finding.follow_up_required && (
                              <div>
                                <label
                                  style={{
                                    display: "block",
                                    fontSize: isMobile ? "15px" : "14px",
                                    fontWeight: "500",
                                    marginBottom: "6px",
                                  }}
                                >
                                  Follow-up Date
                                </label>
                                <input
                                  type="date"
                                  id={`follow_up_date_${index}`}
                                  name={`follow_up_date_${index}`}
                                  value={finding.follow_up_date || ""}
                                  onChange={(e) =>
                                    updateFinding(
                                      index,
                                      "follow_up_date",
                                      e.target.value
                                    )
                                  }
                                  min={new Date().toISOString().split("T")[0]}
                                  style={inputStyle}
                                />
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {errors.submit && (
              <div
                style={{
                  color: "#ef4444",
                  fontSize: isMobile ? "15px" : "14px",
                  marginTop: "16px",
                  padding: isMobile ? "16px" : "12px",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "6px",
                }}
              >
                {errors.submit}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <div
            style={{
              fontSize: isMobile ? "13px" : "13px",
              color: "#6b7280",
              textAlign: "center",
            }}
          >
            {selectedFindings.length > 0 && (
              <>{selectedFindings.length} finding(s) selected</>
            )}
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              width: isMobile ? "100%" : "auto",
            }}
          >
            <button
              onClick={onClose}
              style={{
                ...buttonStyle("secondary"),
                flex: isMobile ? "1" : "none",
              }}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              style={{
                ...buttonStyle("primary"),
                opacity: isSaving ? 0.7 : 1,
                cursor: isSaving ? "not-allowed" : "pointer",
                flex: isMobile ? "1" : "none",
              }}
              disabled={isSaving || selectedFindings.length === 0}
            >
              {isSaving ? "💾 Saving..." : "💾 Save Findings"}
            </button>
          </div>
        </div>
      </div>

      <style>
        {`
         input:focus, select:focus, textarea:focus {
           border-color: #3b82f6 !important;
           box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
         }
         
         button:hover:not(:disabled) {
           transform: translateY(-1px);
           box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
         }
         
         @media (max-width: 770px) {
           button:hover:not(:disabled) {
             transform: none;
           }
         }
       `}
      </style>
    </div>
  );
};

export default BodyMapModal;
